/**
 * Вход.
 *
 * Исполнители сюда не попадают: они открывают приложение из Telegram,
 * и вход происходит молча по подписи. Эта форма — для входа в админку
 * из обычного браузера, где лежат деньги, поэтому второй фактор обязателен.
 */
import { useState, type FormEvent } from 'react';
import { useAuth } from '../lib/auth';
import { ApiError } from '../lib/api';

export function LoginPage({ error: authError }: { error: string | null }) {
  const { loginWithPassword, loginDev } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needTotp, setNeedTotp] = useState(false);
  const [error, setError] = useState<string | null>(authError);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await loginWithPassword(username, password, totp || undefined);
    } catch (err) {
      // Сервер просит код подтверждения — показываем поле, а не ошибку
      if (err instanceof ApiError && err.code === 'TOTP_REQUIRED') {
        setNeedTotp(true);
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : 'Не удалось войти');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="h-full grid place-items-center px-4">
      <form onSubmit={submit} className="w-full max-w-[340px]">
        <div className="text-center mb-8">
          <div className="text-[26px] tracking-[0.01em] font-extrabold text-accent2">
            Конторка
          </div>
          <div className="text-[13px] text-muted2 mt-2">Вход в систему</div>
        </div>

        <div className="card space-y-3">
          <input
            className="input"
            placeholder="Логин"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <input
            className="input"
            type="password"
            placeholder="Пароль"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {needTotp && (
            <input
              className="input mono tracking-[0.3em] text-center"
              placeholder="000 000"
              inputMode="numeric"
              maxLength={8}
              autoFocus
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\s/g, ''))}
            />
          )}

          {error && (
            <div className="text-[12.5px] text-dang bg-dang/10 rounded-lg px-3 py-2 leading-snug">
              {error}
            </div>
          )}

          <button className="btn btn-primary w-full py-3" disabled={busy}>
            {busy ? 'Проверяю…' : needTotp ? 'Подтвердить' : 'Войти'}
          </button>
        </div>

        <div className="text-center text-[11.5px] text-muted2 mt-5 leading-relaxed">
          Логин и пароль выдаёт администратор.<br />
          Если Telegram работает — можно войти через бота, без пароля.
        </div>

        {/* Только для локальной разработки.
            В собранной версии этого блока не существует: Vite вырезает его
            целиком, потому что import.meta.env.DEV становится false. */}
        {import.meta.env.DEV && (
          <div className="mt-6 pt-5 border-t border-line">
            <button
              type="button"
              className="btn w-full"
              disabled={busy}
              onClick={() => {
                setError(null);
                setBusy(true);
                loginDev()
                  .catch((err) =>
                    setError(err instanceof Error ? err.message : 'Не удалось войти'),
                  )
                  .finally(() => setBusy(false));
              }}
            >
              Войти администратором
            </button>
            <div className="text-center text-[11px] text-muted2 mt-2.5 leading-relaxed">
              Режим разработки. Telegram требует HTTPS и не открывает приложение
              с localhost — кнопка нужна, пока нет домена.
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
